import React from 'react'

import {hpcAttrs} from '@github-ui/hpc'

import {BrandPageContent} from '../../../../brand/components/BrandPage'

// Custom components
import {DeepDive} from './_components/DeepDive/DeepDive'
import {FooterVisual} from './_components/FooterVisual/FooterVisual'
import {Gallery} from './_components/Gallery/Gallery'
import {Hero} from './_components/Hero/Hero'
import {Highlight} from './_components/Highlight/Highlight'
import {Media} from './_components/Media/Media'
import {Overview} from './_components/Overview/Overview'
import {PageDivider} from './_components/PageDivider/PageDivider'
import {River} from './_components/River/River'
import {RiverAccordion} from './_components/RiverAccordion/RiverAccordion'
import {WebGLHero} from './_components/WebGL/WebGLHero/WebGLHero'

import {Nav} from './_shared/Nav/Nav'
import {NavBack} from './_shared/NavBack/NavBack'
import {Product} from './_shared/Product/Product'

import {universeRecapPage} from './content'
import Styles from './Index.module.css'

const PAGE_DIVIDER_TYPES: Partial<Record<string, 1 | 2 | 3>> = {
  foundations: 1,
  agents: 2,
  security: 3,
}

export default function EventsUniverseRecapIndex() {
  return (
    <BrandPageContent definition={universeRecapPage} className={Styles.Page} colorMode="light">
      {({nav, sections}) => {
        const hpcSectionIndex = sections.findIndex(({components}) => components.length > 0)
        const themeMap: Record<string, string> = {}
        for (const {section, components} of sections) {
          const sectionId = section.fields.htmlId
          const theme = components.at(0)?.content.fields.label?.fields.color || 'green'
          if (sectionId) themeMap[sectionId] = theme
        }

        return sections.map(({section, components}, sectionIndex) => {
          //
          // Section
          //
          const sectionId = section.fields.htmlId || '' // Used for deeplinking
          const location = sectionId ? `section_${sectionId}` : `section_${sectionIndex + 1}` // Used for analytics
          const theme = components.at(0)?.content.fields.label?.fields.color || 'green' // Used for setting a theme color
          return (
            <React.Fragment key={section.sys?.id || sectionIndex}>
              {/* Render BACK TO UNIVERSE before the first section */}
              {sectionIndex === 0 && nav && <NavBack content={nav} />}

              <section
                className={Styles.Section}
                {...(sectionId && {id: sectionId})}
                {...(theme && {'data-theme': theme})}
              >
                <div className={Styles.Container}>
                  {components.map(({content: component, nestedContent}, contentIndex) => {
                    //
                    // Component
                    //
                    const customComponent = component?.fields.id || '' // ID is used for choosing custom component
                    const productPlacement = component.fields.media?.at(0)?.fields.id
                    const footerFallbackImage = component.fields.media?.find(
                      asset => asset.fields.asset.fields.file.details?.image,
                    )
                    const pageDividerType =
                      customComponent === 'Media' && !component.fields.media?.length
                        ? PAGE_DIVIDER_TYPES[sectionId]
                        : undefined

                    // Add space between components where an asset touches the edge
                    const previousComponent =
                      contentIndex > 0 ? components[contentIndex - 1]?.content.fields.id || '' : ''
                    const shouldAddSpacing =
                      (['River', 'RiverAccordion', 'Media'].includes(previousComponent) &&
                        customComponent !== 'Highlight') ||
                      (previousComponent === 'Highlight' &&
                        ['River', 'RiverAccordion', 'Media'].includes(customComponent))

                    return (
                      <div
                        key={component?.sys?.id || contentIndex}
                        className={Styles.Component}
                        {...hpcAttrs({enabled: sectionIndex === hpcSectionIndex && contentIndex === 0})}
                        {...(shouldAddSpacing && {'data-spacing': 'top'})}
                      >
                        {/* DeepDive */}
                        {customComponent === 'DeepDive' && (
                          <DeepDive content={component} links={nestedContent ?? []} location={location} />
                        )}

                        {/* Gallery */}
                        {customComponent === 'Gallery' && <Gallery content={component} sectionIndex={sectionIndex} />}

                        {/* Hero */}
                        {customComponent === 'Hero' && (
                          <Hero
                            content={component}
                            nestedContent={nestedContent ?? []}
                            location={location}
                            sectionId={sectionId}
                            sectionIndex={sectionIndex}
                          />
                        )}

                        {/* Highlight */}
                        {customComponent === 'Highlight' && (
                          <Highlight content={component} location={location} sectionIndex={sectionIndex} />
                        )}

                        {/* Media */}
                        {customComponent === 'Media' &&
                          (pageDividerType ? (
                            <PageDivider type={pageDividerType} />
                          ) : sectionId === 'changelog' ? (
                            <FooterVisual fallbackImage={footerFallbackImage} />
                          ) : sectionIndex === 0 ? (
                            <WebGLHero
                              fallback={
                                <Product placement={productPlacement} variant="media">
                                  <Media
                                    content={component}
                                    location={location}
                                    sectionIndex={sectionIndex}
                                    loading="eager"
                                  />
                                </Product>
                              }
                            />
                          ) : (
                            <Product placement={productPlacement} variant="media">
                              <Media
                                content={component}
                                location={location}
                                sectionIndex={sectionIndex}
                                loading="lazy"
                              />
                            </Product>
                          ))}

                        {/* Overview */}
                        {customComponent === 'Overview' && (
                          <Overview
                            content={component}
                            list={nestedContent ?? []}
                            location={location}
                            index={contentIndex}
                          />
                        )}

                        {/* River */}
                        {customComponent === 'River' && (
                          <River
                            content={component}
                            location={location}
                            index={contentIndex}
                            sectionIndex={sectionIndex}
                          />
                        )}

                        {/* RiverAccordion */}
                        {customComponent === 'RiverAccordion' && (
                          <RiverAccordion
                            content={component}
                            items={nestedContent ?? []}
                            location={location}
                            index={contentIndex}
                            sectionIndex={sectionIndex}
                          />
                        )}
                      </div>
                    )
                  })}
                </div>
              </section>

              {/* Render nav after first section */}
              {sectionIndex === 0 && nav && <Nav content={nav} themeMap={themeMap} />}
            </React.Fragment>
          )
        })
      }}
    </BrandPageContent>
  )
}
